import React, { useState, useRef, useEffect } from 'react';

const ChatArea = ({ 
  conversation, 
  currentUser, 
  onSendMessage, 
  onStartVideoCall,
  messages = []
}) => {
  const [messageText, setMessageText] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Scroll to bottom of messages when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      onSendMessage({
        type: 'text',
        content: messageText.trim(),
        timestamp: new Date().toISOString()
      });
      setMessageText('');
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const fileType = getFileType(file.type);
      
      onSendMessage({
        type: fileType,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        content: event.target.result,
        timestamp: new Date().toISOString()
      });
    };
    
    reader.readAsDataURL(file);
    setAttachmentMenuOpen(false);
  };
  
  const getFileType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'file';
  };
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const renderMessage = (message) => {
    const isCurrentUser = message.senderId === currentUser.id;
    
    return (
      <div 
        key={message.id} 
        className={`message ${isCurrentUser ? 'sent' : 'received'}`}
      >
        {!isCurrentUser && (
          <img 
            src={message.senderAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(message.senderName)}&background=random`} 
            alt={message.senderName} 
            className="message-avatar"
          />
        )}
        
        <div className="message-content">
          {!isCurrentUser && (
            <div className="message-sender">{message.senderName}</div>
          )}
          
          {message.type === 'text' && (
            <div className="message-bubble">
              <p>{message.content}</p>
              <span className="message-time">{formatTime(message.timestamp)}</span>
            </div>
          )}
          
          {message.type === 'image' && (
            <div className="message-bubble image-message">
              <img src={message.content} alt="Shared" className="shared-image" />
              <span className="message-time">{formatTime(message.timestamp)}</span>
            </div>
          )}
          
          {message.type === 'video' && (
            <div className="message-bubble video-message">
              <video controls className="shared-video">
                <source src={message.content} type={message.fileType} />
                Your browser does not support the video tag.
              </video>
              <span className="message-time">{formatTime(message.timestamp)}</span>
            </div>
          )}
          
          {message.type === 'pdf' && (
            <div className="message-bubble file-message">
              <div className="file-preview">
                <i className="fas fa-file-pdf file-icon"></i>
                <div className="file-info">
                  <span className="file-name">{message.fileName}</span>
                  <span className="file-size">{formatFileSize(message.fileSize)}</span>
                </div>
              </div>
              <a 
                href={message.content} 
                download={message.fileName}
                className="download-button"
              >
                <i className="fas fa-download"></i>
              </a>
              <span className="message-time">{formatTime(message.timestamp)}</span>
            </div>
          )}
          
          {message.type === 'file' && (
            <div className="message-bubble file-message">
              <div className="file-preview">
                <i className="fas fa-file file-icon"></i>
                <div className="file-info">
                  <span className="file-name">{message.fileName}</span>
                  <span className="file-size">{formatFileSize(message.fileSize)}</span>
                </div>
              </div>
              <a 
                href={message.content} 
                download={message.fileName}
                className="download-button"
              >
                <i className="fas fa-download"></i>
              </a>
              <span className="message-time">{formatTime(message.timestamp)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    else return (bytes / 1073741824).toFixed(1) + ' GB';
  };
  
  // If no conversation is selected
  if (!conversation) {
    return (
      <div className="chat-area empty-chat">
        <div className="empty-chat-content">
          <div className="empty-chat-icon">
            <i className="fas fa-comments"></i>
          </div>
          <h2>Welcome to CZone</h2>
          <p>Select a conversation or start a new one to begin chatting</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="chat-area">
      <div className="chat-header">
        <div className="chat-contact-info">
          <img 
            src={conversation.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.name)}&background=random`} 
            alt={conversation.name} 
            className="avatar"
          />
          <div>
            <h3>{conversation.name}</h3>
            <span className="status">{conversation.isOnline ? 'Online' : 'Offline'}</span>
          </div>
        </div>
        <div className="chat-actions">
          <button 
            className="icon-button" 
            onClick={onStartVideoCall}
            aria-label="Start video call"
          >
            <i className="fas fa-video"></i>
          </button>
          <button className="icon-button" aria-label="More options">
            <i className="fas fa-ellipsis-v"></i>
          </button>
        </div>
      </div>
      
      <div className="messages-container">
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-input-area">
        <div className="input-actions">
          <button 
            className="icon-button"
            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
            aria-label="Emoji picker"
          >
            <i className="far fa-smile"></i>
          </button>
          
          <div className="attachment-container">
            <button 
              className="icon-button"
              onClick={() => setAttachmentMenuOpen(!attachmentMenuOpen)}
              aria-label="Attach file"
            >
              <i className="fas fa-paperclip"></i>
            </button>
            
            {attachmentMenuOpen && (
              <div className="attachment-menu">
                <button 
                  className="attachment-option"
                  onClick={() => {
                    fileInputRef.current.accept = "image/*";
                    fileInputRef.current.click();
                  }}
                >
                  <i className="fas fa-image"></i>
                  <span>Image</span>
                </button>
                <button 
                  className="attachment-option"
                  onClick={() => {
                    fileInputRef.current.accept = "application/pdf";
                    fileInputRef.current.click();
                  }}
                >
                  <i className="fas fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
                <button 
                  className="attachment-option"
                  onClick={() => {
                    fileInputRef.current.accept = "video/*";
                    fileInputRef.current.click();
                  }}
                >
                  <i className="fas fa-video"></i>
                  <span>Video</span>
                </button>
                <button 
                  className="attachment-option"
                  onClick={() => {
                    fileInputRef.current.accept = "*/*";
                    fileInputRef.current.click();
                  }}
                >
                  <i className="fas fa-file"></i>
                  <span>Document</span>
                </button>
              </div>
            )}
            
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              onChange={handleFileSelect}
            />
          </div>
        </div>
        
        <form onSubmit={handleSendMessage} className="message-form">
          <input
            type="text"
            placeholder="Type a message"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            className="message-input"
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={!messageText.trim()}
            aria-label="Send message"
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatArea;
